import React from 'react';

/**
 * Type for camera controls reference (OrbitControls)
 */
export interface CameraControlsRef {
  reset: () => void;
  update: () => void;
  object: {
    position: {
      distanceTo: (target: any) => number;
      set: (x: number, y: number, z: number) => void;
    };
    up: {
      set: (x: number, y: number, z: number) => void;
    };
    lookAt: (target: any) => void;
  };
  target: any;
}

/**
 * Reset camera view to default slicer orientation
 * @param cameraControlsRef - Reference to the camera controls
 */
export const resetView = (cameraControlsRef: React.MutableRefObject<CameraControlsRef | null>): void => {
  if (cameraControlsRef.current) {
    // Reset the orbit controls to default position
    cameraControlsRef.current.reset();
    
    // Additional reset for a typical slicer view angle
    setTimeout(() => {
      if (cameraControlsRef.current && cameraControlsRef.current.object) {
        const camera = cameraControlsRef.current.object;
        const center = cameraControlsRef.current.target;
        
        // Use a closer distance for the default view
        const currentDistance = camera.position.distanceTo(center);
        const targetDistance = currentDistance * 0.7; // 70% of the current distance for closer view
        
        // Position camera using spherical coordinates for proper slicer orientation
        // with XY as the build plate and Z pointing up
        const theta = Math.PI / 4; // 45 degrees around Z axis
        const phi = Math.PI / 6;   // 30 degrees from the XY plane
        
        camera.position.set(
          center.x + targetDistance * Math.sin(phi) * Math.cos(theta),
          center.y + targetDistance * Math.sin(phi) * Math.sin(theta),
          center.z + targetDistance * Math.cos(phi)
        );
        
        // Ensure up vector is along Z axis (build plate is XY plane)
        camera.up.set(0, 0, 1);
        
        // Look at the center
        camera.lookAt(center);
        
        // Update controls
        cameraControlsRef.current.update();
      }
    }, 10);
  }
};

/**
 * Create intersection observer for viewport visibility
 * @param setIsVisible - State setter for visibility
 * @param cardRef - Reference to the card element
 * @returns Cleanup function
 */
export const createViewportObserver = (
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>,
  cardRef: React.RefObject<HTMLDivElement>
): (() => void) => {
  const observer = new IntersectionObserver(
    ([entry]) => {
      setIsVisible(entry.isIntersecting);
    },
    { threshold: 0.1 } // 10% visibility triggers the callback
  );
  
  if (cardRef.current) {
    observer.observe(cardRef.current);
  }
  
  // Return cleanup function
  return () => {
    if (cardRef.current) {
      observer.unobserve(cardRef.current);
    }
  };
}; 